import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatPaymentMethodChoice {
  id: string;
  name: string;
  description?: string;
  type?: string;
  apexxTokenId?: string;
}

export interface ChatPaymentMethodAnswer {
  id: string;
  name: string;
  basketItemId: string;
  type?: string;
  apexxTokenId?: string;
  /** Card nickname entered inline when id === 'apexx_add_new', replacing the
   * separate "Add New Payment Card" modal popup for the chatbot flow. */
  cardLabel?: string;
}

/**
 * Renders the payment-method picker widget inside the chat widget — a plain
 * dropdown of the basket item's available payment methods. Only ever shown
 * when there are 2+ options and none selected yet (see
 * chat-orchestrator.service.ts's extractPaymentMethodPrompt — a single option
 * is auto-selected by the model itself, no widget needed). Unlike the
 * booking_info field widgets this isn't per-passenger and has no Skip — a
 * payment method is always mandatory before booking.
 */
@Component({
  selector: 'app-chat-payment-input',
  standalone: false,
  templateUrl: './chat-payment-input.component.html',
  styleUrls: ['./chat-payment-input.component.scss']
})
export class ChatPaymentInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() choices: ChatPaymentMethodChoice[] = [];
  @Input() basketItemId = '';
  @Input() productName?: string;
  @Input() defaultOptionId?: string;
  @Output() answer = new EventEmitter<ChatPaymentMethodAnswer>();

  /** Bound via [(ngModel)] on the <select> — holds the payment method id, or
   * '' when nothing is chosen yet. */
  selectedId = '';
  /** Nickname for a new Apexx card, entered inline once 'Add new card' is
   * selected — replaces the separate OBT "Add New Payment Card" modal for
   * the chat flow (the actual hosted card-entry page still opens as its own
   * popup for PCI reasons, this step is just the friendly label). */
  cardLabel = '';
  /** Human-readable summary of what was submitted, shown in place of the
   * control once this widget instance is locked. */
  chosenSummary: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    this.selectedId =
      this.defaultOptionId && this.choices.some((c) => c.id === this.defaultOptionId)
        ? this.defaultOptionId
        : '';
    this.cardLabel = '';
    this.chosenSummary = null;
  }

  get isAddingNewCard(): boolean {
    return this.selectedId === 'apexx_add_new';
  }

  get isValid(): boolean {
    if (this.isAddingNewCard) {
      return !!this.cardLabel.trim();
    }
    return !!this.selectedId;
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    const choice = this.choices.find((c) => c.id === this.selectedId);
    if (!choice) {
      return;
    }
    if (choice.type !== 'apexx_new_card' && choice.id !== 'apexx_add_new') {
      this.chosenSummary = choice.name;
    }
    this.answer.emit({
      id: choice.id,
      name: choice.name,
      basketItemId: this.basketItemId,
      type: choice.type,
      apexxTokenId: choice.apexxTokenId,
      cardLabel: this.isAddingNewCard ? this.cardLabel.trim() : undefined
    });
  }
}
